'use client';
import { useEffect, useRef, type RefObject } from 'react';
import { Check, Compass, Hammer, Mic, Pause, Phone, Play, SlidersHorizontal, Workflow, X, type LucideIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { StatusMark } from '../../components/ui/StatusMark';
import { useToast } from '../../components/ui/Toast';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup,
  DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/shadcn/dropdown-menu';
import { formatClock } from '../../lib/format';
import { interpolate, useTranslation } from '../../lib/i18n';
import { useSetAgentPaused } from '../../lib/mutations';
import type { BrainWorkMode, SlashCommandDef } from '../../lib/types';
import { useVoiceCallController } from '../voice/callController';
import type { useDictation } from '../voice/useDictation';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';

/** The composer's single "Options" control, immediately left of send/stop on every surface (full page, dock,
 *  phone). It replaces two separate buttons (the work-mode pill and the phone glyph) with one menu, so the
 *  composer row and the conversation keep their shape while every per-message action has a home:
 *
 *  - "Work mode": the daemon catalog's `kind:'mode'` commands as a radio group. Selecting runs the SAME
 *    `runSlash` path the slash menu and `/help` use, so the toast, the daemon command and every side effect
 *    stay identical; this is a second door onto the composer, never a second implementation.
 *  - "Voice" (only when voice is available to the account): call, or dictate, which writes the words
 *    into the draft at the caret while they are spoken. Dictating never sends; the composer's own send
 *    button stays available meanwhile and finishes the dictation first.
 *  - "Shared conversation" (only once someone was invited): pause or resume the agent.
 *
 *  The trigger carries one general glyph, the sliders the slash menu also uses for a mode, because the menu
 *  holds more than the mode. The mode (it changes what the agent may do) is still never a hidden switch: the
 *  accent tint marks plan and workflow as "on", build stays quiet, and `title` and `aria-label` name the mode. Opening is click/keyboard only; Radix owns the
 *  keyboard grammar. On a 390px phone the label collapses to the glyph.
 *
 *  While a dictation listens or finishes the trigger is replaced, in the same row slot, by a compact
 *  status (pulsing dot and elapsed time, done, cancel) so the composer's height and layout do not move. */
const WORK_MODE_GLYPHS: Record<BrainWorkMode, LucideIcon> = { build: Hammer, plan: Compass, workflow: Workflow };

/** Both conversation menus use the same authorized catalog and mode command path. */
export function WorkModeMenuGroup({ testIdPrefix }: { testIdPrefix: string }) {
  const { t } = useTranslation();
  const { commands, runSlash, workMode, modeSwitching } = useBrainChat();
  // The mode is the owner's: a guest sees it selected and cannot pick another.
  const { guest } = useBrainChatStatus();
  const modes = commands.filter((command): command is SlashCommandDef & { name: BrainWorkMode } =>
    command.kind === 'mode' && command.name in WORK_MODE_GLYPHS);
  return (
    <DropdownMenuRadioGroup value={workMode} aria-label={t.brainChat.workModeGroup}>
      {modes.map(command => (
        <DropdownMenuRadioItem key={command.name} value={command.name} disabled={!workMode || modeSwitching || guest}
          aria-label={t.brainChat.workMode[command.name]} data-testid={`${testIdPrefix}-${command.name}`}
          onSelect={() => { if (command.name !== workMode) runSlash(command); }}>
          <MenuRow icon={WORK_MODE_GLYPHS[command.name]} label={t.brainChat.workMode[command.name]}
            hint={t.brainChat.commandHints[command.name]} />
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );
}

/** A shared conversation's agent pause, shown to every member and switchable by its owner or an administrator
 *  (the daemon's `capabilities.pauseAgent`); other guests see it disabled. A conversation nobody
 *  shares has no group. The row names the action, so a paused conversation offers to resume. */
function AgentPauseMenuGroup() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { activeSessionId } = useBrainChat();
  const { membership } = useBrainChatStatus();
  const { mutateAsync } = useSetAgentPaused();
  if (!membership || membership.participants.length === 0 || !activeSessionId) return null;
  const paused = membership.agentPaused;
  return (
    <>
      <DropdownMenuSeparator />
      <div role="group" aria-label={t.chat.agentGroup}>
        <DropdownMenuLabel>{t.chat.agentGroup}</DropdownMenuLabel>
        <DropdownMenuItem disabled={!membership.capabilities.pauseAgent} data-testid="chat-agent-pause" onSelect={() => {
            // The menu closes on select and unmounts this row, so the refusal is reported from the promise.
            void mutateAsync({ sessionId: activeSessionId, paused: !paused })
              .catch((error: unknown) => toast(t.chat.agentPauseFailed, 'error', { error }));
          }}>
          <MenuRow icon={paused ? Play : Pause} label={paused ? t.chat.resumeAgent : t.chat.pauseAgent}
            hint={paused ? t.chat.resumeAgentHint : t.chat.pauseAgentHint} />
        </DropdownMenuItem>
      </div>
    </>
  );
}

/** One menu row: glyph, name and a one-line hint, shared by the mode, voice and agent groups so all read alike. */
function MenuRow({ icon: RowIcon, label, hint }: { icon: LucideIcon; label: string; hint: string }) {
  return <>
    <RowIcon size={14} aria-hidden className="mt-0.5 shrink-0" />
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate">{label}</span>
      <span className="truncate text-meta text-muted-foreground">{hint}</span>
    </span>
  </>;
}

/** The dictation the composer owns; the send button needs it to finish the dictation before it sends. */
type ComposerDictation = Pick<ReturnType<typeof useDictation>, 'state' | 'elapsedMs' | 'start' | 'stop' | 'cancel'>;

export function ComposerActions({ variant, composerRef, callActionRef, dictation }: {
  variant: 'full' | 'compact';
  composerRef: RefObject<HTMLTextAreaElement | null>;
  callActionRef: RefObject<HTMLButtonElement | null>;
  dictation: ComposerDictation;
}) {
  const { t } = useTranslation();
  const { workMode, modeSwitching } = useBrainChat();
  const voice = useVoiceCallController();
  const callSelected = useRef(false);
  const dictating = dictation.state;
  const listening = dictating === 'connecting' || dictating === 'listening';
  const { cancel } = dictation;
  useEffect(() => {
    if (!listening) return;
    const onKey = (event: KeyboardEvent): void => { if (event.key === 'Escape') cancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [listening, cancel]);

  const modeLabel = workMode ? t.brainChat.workMode[workMode] : t.common.loading;
  const title = interpolate(t.brainChat.actionsTitle, { mode: modeLabel });
  const radius = variant === 'full' ? 'rounded-xl' : 'rounded-lg';

  if (dictating !== 'idle') {
    return (
      // One pill is both the recording indicator and the Done button, so a 320 px phone keeps room for the
      // draft: a separate check button beside it squeezed the textarea to a sliver. The group is one control
      // row, so the pill and the cancel beside it take the shared default step (36px, 44px on a coarse
      // pointer) instead of stating a height of their own.
      <div className="flex shrink-0 items-center gap-1" data-testid="chat-dictation" data-state={dictating}>
        <span className="sr-only" role="status">{listening ? t.voice.dictation.recording : t.voice.dictation.transcribing}</span>
        {listening ? (
          <Button
            type="button"
            variant="ghost"
            data-testid="chat-dictation-stop"
            aria-label={`${t.voice.dictation.stop}, ${formatClock(dictation.elapsedMs)}`}
            title={t.voice.dictation.stop}
            onClick={() => { dictation.stop(); composerRef.current?.focus(); }}
            className={`gap-1.5 border-primary/40 bg-primary/10 px-2 text-small tabular-nums text-primary hover:bg-primary/20 hover:text-primary disabled:hover:bg-primary/10 disabled:hover:text-primary ${radius}`}
          >
            <span className="size-2 animate-pulse rounded-full bg-destructive" aria-hidden />
            {/* A phone keeps the dot and the check; the time joins them where the row has room. */}
            <span className="hidden md:inline">{formatClock(dictation.elapsedMs)}</span>
            <Check size={14} aria-hidden />
          </Button>
        ) : (
          <StatusMark kind="working" tone="accent" label={t.voice.dictation.transcribing} />
        )}
        <Button type="button" variant="ghost" size="icon" icon={X} data-testid="chat-dictation-cancel"
          aria-label={t.voice.dictation.cancel} title={t.voice.dictation.cancel} onClick={dictation.cancel} className={radius} />
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          data-testid="chat-actions"
          data-work-mode={workMode}
          aria-label={title}
          title={title}
          disabled={modeSwitching}
          aria-busy={modeSwitching}
          // A phone gets the glyph alone, square, so the composer keeps its width for the text; the
          // label joins it from the app's phone breakpoint up (PHONE_MAX_WIDTH = 767 → `md:`). Only the
          // WIDTH is the caller's: the height and its coarse floor belong to the shared step, the same one
          // the attach and send/stop controls beside it take. It used to state `h-9 w-9` of its own, which a
          // studio skin rule masked and which measured 36 beside a 44px attach once that rule went.
          className={`w-9 shrink-0 justify-center gap-1.5 px-0 text-small md:w-auto md:px-2 ${radius} ${
            (!workMode || workMode === 'build')
              ? 'border-border text-muted-foreground hover:bg-accent hover:text-foreground'
              : 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary'
          }`}
        >
          <SlidersHorizontal size={15} aria-hidden />
          <span className="hidden md:inline">{t.brainChat.actions}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="min-w-56" data-testid="chat-actions-menu"
        onCloseAutoFocus={event => {
          if (!callSelected.current) return;
          event.preventDefault(); callActionRef.current?.focus();
        }}>
        <DropdownMenuLabel>{t.brainChat.workModeGroup}</DropdownMenuLabel>
        <WorkModeMenuGroup testIdPrefix="chat-work-mode" />
        {voice?.available && (
          <>
            <DropdownMenuSeparator />
            <div role="group" aria-label={t.voice.actionsGroup}>
              <DropdownMenuLabel>{t.voice.actionsGroup}</DropdownMenuLabel>
              <DropdownMenuItem disabled={voice.active} onSelect={() => { callSelected.current = true; voice.start(); }} data-testid="voice-call-button">
                <MenuRow icon={Phone} label={t.voice.call.start} hint={t.voice.call.hint} />
              </DropdownMenuItem>
              <DropdownMenuItem disabled={voice.active} onSelect={() => { void dictation.start(); }} data-testid="chat-dictate">
                <MenuRow icon={Mic} label={t.voice.dictation.start} hint={t.voice.dictation.hint} />
              </DropdownMenuItem>
            </div>
          </>
        )}
        <AgentPauseMenuGroup />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
